<template data-test-role="paymentusCheckoutDialog">
    <div>
        <div class="d-flex">
            <pc-label
                class="h4"
                v-bind:label="headerLabel" />
            <pc-button
                v-if="!paymentIsProcessing && !cancelClicked"
                id="CancelButton"
                link
                data-test-role="paymentusCancelButton"
                class="ms-auto"
                v-on:click="cancel">
                {{ cancelButtonLabel }}
            </pc-button>
        </div>
        <div>
            <span class="fw-bold">
                {{ paymentAmountLabel }}
            </span>
            <span data-test-role="amount-to-pay">
                {{ displayamount }}
            </span>
        </div>
        <div>
            <span class="fw-bold">
                {{ billingEmailLabel }}
            </span>
            <span data-test-role="billing-email">
                {{ billingemail }}
            </span>
        </div>
        <hr />
        <div class="d-flex">
            <pc-help-icon
                v-if="!displayHelp && !paymentIsProcessing"
                v-on:click="displayHelp = !displayHelp" />
            <pc-alert
                v-if="displayHelp && !paymentIsProcessing"
                v-model:shown="displayHelp"
                v-bind:message="helpText"
                dismissible />
        </div>
        <div class="d-flex justify-content-center">
            <pc-loader
                v-bind:loading="paymentIsProcessing || cancelClicked"
                v-bind:message="pleaseWaitMessage">
                <iframe
                    id="PaymentusFrame"
                    ref="PaymentusFrame"
                    allowfullscreen="false"
                    v-bind:src="paymentusFrameSrc"
                    v-bind:height="paymentusFrameHeight"
                    v-bind:width="paymentusFrameWidth"
                    v-bind:title="paymentusFrameTitle"
                    frameborder="0"
                    marginwidth="0"
                    marginheight="0"
                    scrolling="auto" />
            </pc-loader>
        </div>
    </div>
</template>

<script>
    import { CHARGE_STATUS } from "EPayments/ePayments";
    import pcButton from "Components/pc-button.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/paymentusDialog.i18n";

    const EPAYMENT_TYPE = {
        CreditCard: "CC",
        DebitCard: "DC",
        ElectronicCheck: "DD"
    };
    const DEFAULT_HEIGHT = {
        Card: "640",
        Check: "800",
        buffer: "24"
    };
    const DEFAULT_WIDTH = "350";

    export default {
        components: { pcButton },
        props: {
            driver: { type: Object, required: true },
            url: { type: String, required: true },
            token: { type: String, required: true },
            epaytype: { type: String, required: true },
            displayamount: { type: String, required: true },
            billingemail: { type: String, required: true }
        },
        data() {
            return {
                displayHelp: false,
                paymentusFrameHeight:
                    this.epaytype === EPAYMENT_TYPE.ElectronicCheck ? DEFAULT_HEIGHT.Check : DEFAULT_HEIGHT.Card,
                paymentusFrameWidth: DEFAULT_WIDTH, // Make dynamic?
                paymentIsProcessing: false,
                cancelClicked: false,
                pleaseWaitMessage: ""
            };
        },
        computed: {
            helpText() {
                return this.iFrameHelpText;
            },
            headerLabel() {
                return this.paymentIsProcessing ? this.paymentProcessingDialogHeader : this.paymentInputDialogHeader;
            },
            paymentusFrameSrc() {
                return this.url + this.token;
            },
            paymentusEventOrigin() {
                // For the frameHeight postMessage, the event.origin will be the part of the
                //  paymentusFrameSrc url up to but not including the first single slash.
                // There will be two consecutive single slashes after https: at the beginning.
                // So the event.origin will be the slash-join of the first four url "parts":
                //  https: <empty> <empty> subdomain.domain.com
                const urlParts = this.url.split("/");
                return urlParts.slice(0, 3).join("/");
            },
            // Localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        async mounted() {
            this.delayedSubmit();
            window.addEventListener("message", this.receiveMessage, false);
        },
        methods: {
            delayedSubmit() {
                this.timeout = setTimeout(this._checkChargeStatus.bind(this), 2000);
                this.checkingChargeStatus = false;
            },
            onValidated(prop, validationResult) {
                this.validationResults[prop] = validationResult;
            },
            receiveMessage(event) {
                // Verify the origin of the message
                if (event.origin === this.paymentusEventOrigin) {
                    // The event.data should be a single key-value pair of the form
                    //  "frameHeight:<value>"
                    if (event.data) {
                        const frameHeight = event.data.toString().split(":");
                        if (frameHeight[0] === "frameHeight") {
                            let height = parseInt(frameHeight[1]);
                            if (!isNaN(height)) {
                                // Add a buffer to the returned height value
                                height += parseInt(DEFAULT_HEIGHT.buffer);
                                this.paymentusFrameHeight = height.toString();
                            }
                        }
                    }
                }
            },
            setPleaseWaitMessage() {
                let message = "";
                if (this.paymentIsProcessing) {
                    if (this.cancelClicked) {
                        message = this.cancelUnavailableWaitMessage;
                    } else {
                        message = this.paymentProcessingWaitMessage;
                    }
                } else if (this.cancelClicked) {
                    message = this.cancelPendingWaitMessage;
                }
                this.pleaseWaitMessage = message;
            },
            async _checkChargeStatus() {
                if (!this.driver || !this.driver.chargeId || this.checkingChargeStatus) {
                    return;
                }
                this.checkingChargeStatus = true;
                const result = await this.driver.PaymentusCheckChargeStatus(
                    this.billingemail,
                    this.paymentIsProcessing
                );
                this.checkingChargeStatus = false;
                if (result.Data.PaymentMethodTokenReceived) {
                    this.paymentIsProcessing = true;
                    this.setPleaseWaitMessage();
                }
                if (result.Data.MessageProcessed) {
                    window.removeEventListener("message", this.receiveMessage);
                    await this.$app.closeDialog({
                        resolve: true,
                        resolveValue: {
                            success: result.Data.ReceiptInfo.Status === CHARGE_STATUS.Succeeded,
                            resultData: result.Data
                        }
                    });
                } else {
                    if (this.timeout) {
                        this.timeout = setTimeout(this._checkChargeStatus.bind(this), 100);
                    }
                }
            },
            async cancel() {
                if (this.cancelClicked || this.paymentIsProcessing) {
                    return;
                }
                // Wait to see if we get notification a payment was in process
                this.cancelClicked = true;
                this.setPleaseWaitMessage();
                const waitTimeMs = 5000;
                await new Promise((resolve) => setTimeout(resolve, waitTimeMs));
                if (this.paymentIsProcessing) {
                    this.setPleaseWaitMessage();
                    return;
                }
                if (this.timeout) {
                    clearTimeout(this.timeout);
                    this.timeout = null;
                }
                window.removeEventListener("message", this.receiveMessage);
                this.$app.closeDialog({
                    resolve: true,
                    resolveValue: { success: false }
                });
            }
        }
    };
</script>
